// ---------------------------------------------------------------------------
// paymenthooks.content.ts — Localized content for PaymentHooks integration landing page
// ---------------------------------------------------------------------------
import type { IntegrationRecord } from './types';

export const PAYMENTHOOKS_INTEGRATION: IntegrationRecord = {
  constants: { slug: 'paymenthooks', partnerName: 'PaymentHooks.com' },
  content: {
    en: {
      partnerType: 'Unified Payment Aggregator',
      heroTitle: 'HotelStack.io + PaymentHooks.com Integration',
      heroSubtitle: 'Unified payment gateway aggregator connecting Stripe, PayPal, PaymentHooks, Square, Adyen, and 50+ providers through one API. Multi-currency processing, automatic routing, and PCI compliance built in.',
      benefits: [
        {
          title: '50+ Payment Providers',
          description: 'Connect Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree, and dozens more through a single unified API. Add or switch providers without code changes.',
        },
        {
          title: 'Intelligent Payment Routing',
          description: 'Automatic routing directs each transaction to the optimal provider based on currency, geography, success rates, and processing fees. Maximize acceptance rates while minimizing costs.',
        },
        {
          title: 'PCI DSS Level 1 Compliance',
          description: 'PaymentHooks handles all sensitive card data in their PCI-certified vault. HotelStack.io never touches raw card numbers, ensuring full compliance without the audit burden.',
        },
        {
          title: 'Real-Time Webhooks',
          description: 'Instant webhook notifications for payment success, failure, refund, chargeback, and settlement events. HotelStack.io updates guest folios and booking statuses automatically.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Connect PaymentHooks Account',
          description: 'Create a PaymentHooks.com account and link it to HotelStack.io using your API keys. Setup takes under 5 minutes from the Settings panel.',
        },
        {
          step: 2,
          title: 'Configure Payment Providers',
          description: 'Add your existing Stripe, PayPal, PaymentHooks, or other gateway credentials in the PaymentHooks dashboard. Enable the providers you want to accept.',
        },
        {
          step: 3,
          title: 'Set Routing Rules',
          description: 'Define routing logic: route by currency, guest country, transaction amount, or let the AI optimizer choose the best provider for each payment automatically.',
        },
        {
          step: 4,
          title: 'Accept Payments',
          description: 'Guests see a seamless checkout with all enabled payment methods. PaymentHooks routes each transaction and reports results back to HotelStack.io in real time.',
        },
      ],
      features: [
        {
          title: 'Multi-Currency Processing',
          description: 'Accept payments in 150+ currencies with automatic conversion. Guests pay in their local currency while you settle in yours.',
        },
        {
          title: 'Revenue Reconciliation',
          description: 'Unified settlement reports across all providers. Match every transaction to a booking, folio charge, or refund with one-click reconciliation.',
        },
        {
          title: 'Failover Routing',
          description: 'If a primary payment provider is down or declines, PaymentHooks automatically retries with an alternate provider. Zero lost revenue from gateway outages.',
        },
        {
          title: 'Chargeback Management',
          description: 'Centralized chargeback alerts, evidence submission, and dispute tracking across all providers from a single dashboard.',
        },
        {
          title: 'Tokenized Guest Cards',
          description: 'Securely store guest payment methods as tokens for no-show charges, incidental holds, and repeat bookings without re-entering card details.',
        },
        {
          title: 'Transaction Analytics',
          description: 'Track approval rates, decline reasons, processing costs, and provider performance across all gateways with real-time dashboards.',
        },
      ],
      faqItems: [
        {
          question: 'Does PaymentHooks replace my existing payment gateway?',
          answer: 'No. PaymentHooks sits on top of your existing gateways. You keep your Stripe, PayPal, or PaymentHooks accounts. PaymentHooks unifies them behind one API and adds intelligent routing, failover, and reconciliation.',
        },
        {
          question: 'What are the fees for using PaymentHooks?',
          answer: 'PaymentHooks charges a small per-transaction fee (typically 0.1-0.3%) on top of your existing gateway fees. The intelligent routing often saves more than this by directing transactions to the lowest-cost provider.',
        },
        {
          question: 'Is PaymentHooks PCI compliant?',
          answer: 'Yes. PaymentHooks is PCI DSS Level 1 certified, the highest level of payment security compliance. All card data is tokenized and stored in their certified vault. HotelStack.io never handles raw card numbers.',
        },
        {
          question: 'Can I add new payment providers later?',
          answer: 'Yes. You can add or remove payment providers at any time from the PaymentHooks dashboard without any changes to your HotelStack.io configuration. New providers become available to guests immediately.',
        },
      ],
    },
    ar: {
      partnerType: 'مجمّع بوابات الدفع الموحّد',
      heroTitle: 'تكامل HotelStack.io مع PaymentHooks.com',
      heroSubtitle: 'مجمّع موحّد لبوابات الدفع يربط Stripe و PayPal و PaymentHooks و Square و Adyen وأكثر من ٥٠ مزوّداً عبر API واحد. معالجة متعددة العملات وتوجيه تلقائي وامتثال PCI مدمج.',
      benefits: [
        {
          title: 'أكثر من ٥٠ مزوّد دفع',
          description: 'اربط Stripe و PayPal و PaymentHooks و Square و Adyen و Worldpay و Braintree وعشرات المزوّدين الآخرين عبر API موحّد واحد. أضف مزوّدين أو بدّل بينهم دون أي تغييرات في الكود.',
        },
        {
          title: 'توجيه ذكي للمدفوعات',
          description: 'يوجّه التوجيه التلقائي كل معاملة إلى المزوّد الأمثل بناءً على العملة والموقع الجغرافي ومعدلات النجاح ورسوم المعالجة. عظّم معدلات القبول مع تقليل التكاليف.',
        },
        {
          title: 'امتثال PCI DSS المستوى الأول',
          description: 'يتعامل PaymentHooks مع جميع بيانات البطاقات الحساسة في خزينته المعتمدة PCI. لا يلمس HotelStack.io أرقام البطاقات الخام أبداً، مما يضمن الامتثال الكامل دون عبء التدقيق.',
        },
        {
          title: 'إشعارات Webhook الفورية',
          description: 'إشعارات webhook فورية لأحداث نجاح الدفع والفشل والاسترداد والنزاع والتسوية. يحدّث HotelStack.io حسابات النزلاء وحالات الحجز تلقائياً.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'ربط حساب PaymentHooks',
          description: 'أنشئ حساباً في PaymentHooks.com واربطه بـ HotelStack.io باستخدام مفاتيح API الخاصة بك. يستغرق الإعداد أقل من ٥ دقائق من لوحة الإعدادات.',
        },
        {
          step: 2,
          title: 'تكوين مزوّدي الدفع',
          description: 'أضف بيانات اعتماد Stripe أو PayPal أو PaymentHooks أو بوابات أخرى موجودة لديك في لوحة PaymentHooks. فعّل المزوّدين الذين تريد قبول مدفوعاتهم.',
        },
        {
          step: 3,
          title: 'تعيين قواعد التوجيه',
          description: 'حدّد منطق التوجيه: وجّه حسب العملة أو بلد النزيل أو مبلغ المعاملة، أو دع محسّن الذكاء الاصطناعي يختار أفضل مزوّد لكل دفعة تلقائياً.',
        },
        {
          step: 4,
          title: 'قبول المدفوعات',
          description: 'يرى النزلاء تجربة دفع سلسة مع جميع طرق الدفع المفعّلة. يوجّه PaymentHooks كل معاملة ويرسل النتائج إلى HotelStack.io في الوقت الفعلي.',
        },
      ],
      features: [
        {
          title: 'معالجة متعددة العملات',
          description: 'اقبل المدفوعات بأكثر من ١٥٠ عملة مع تحويل تلقائي. يدفع النزلاء بعملتهم المحلية بينما تسوّي أنت بعملتك.',
        },
        {
          title: 'تسوية الإيرادات',
          description: 'تقارير تسوية موحّدة عبر جميع المزوّدين. طابق كل معاملة مع حجز أو رسوم حساب أو استرداد بنقرة واحدة.',
        },
        {
          title: 'التوجيه الاحتياطي',
          description: 'إذا كان مزوّد الدفع الأساسي معطلاً أو رفض المعاملة، يعيد PaymentHooks المحاولة تلقائياً مع مزوّد بديل. صفر من الإيرادات المفقودة بسبب انقطاع البوابات.',
        },
        {
          title: 'إدارة النزاعات',
          description: 'تنبيهات نزاعات مركزية وتقديم أدلة وتتبع نزاعات عبر جميع المزوّدين من لوحة قيادة واحدة.',
        },
        {
          title: 'بطاقات النزلاء المُرمَّزة',
          description: 'خزّن طرق دفع النزلاء بأمان كرموز لرسوم حالات عدم الحضور والاحتجازات العرضية والحجوزات المتكررة دون إعادة إدخال تفاصيل البطاقة.',
        },
        {
          title: 'تحليلات المعاملات',
          description: 'تتبع معدلات القبول وأسباب الرفض وتكاليف المعالجة وأداء المزوّد عبر جميع البوابات بلوحات قيادة في الوقت الفعلي.',
        },
      ],
      faqItems: [
        {
          question: 'هل يستبدل PaymentHooks بوابة الدفع الموجودة لديّ؟',
          answer: 'لا. يعمل PaymentHooks فوق بواباتك الحالية. تحتفظ بحسابات Stripe و PayPal أو PaymentHooks الخاصة بك. يوحّد PaymentHooks جميعها خلف API واحد ويضيف التوجيه الذكي والتعافي من الأعطال والتسوية.',
        },
        {
          question: 'ما هي رسوم استخدام PaymentHooks؟',
          answer: 'يفرض PaymentHooks رسوماً صغيرة لكل معاملة (عادةً ٠٫١-٠٫٣٪) بالإضافة إلى رسوم بواباتك الحالية. كثيراً ما يوفّر التوجيه الذكي أكثر من ذلك بتوجيه المعاملات إلى أقل مزوّد تكلفةً.',
        },
        {
          question: 'هل PaymentHooks متوافق مع PCI؟',
          answer: 'نعم. PaymentHooks معتمد بمعيار PCI DSS المستوى الأول، وهو أعلى مستوى من الامتثال لأمان الدفع. يتم ترميز جميع بيانات البطاقات وتخزينها في خزينتهم المعتمدة. لا يتعامل HotelStack.io أبداً مع أرقام البطاقات الخام.',
        },
        {
          question: 'هل يمكنني إضافة مزوّدي دفع جدد لاحقاً؟',
          answer: 'نعم. يمكنك إضافة مزوّدي الدفع أو إزالتهم في أي وقت من لوحة PaymentHooks دون أي تغييرات على إعدادات HotelStack.io الخاصة بك. يصبح المزوّدون الجدد متاحين للنزلاء فوراً.',
        },
      ],
    },
    th: {
      partnerType: 'ตัวรวมบริการชำระเงินแบบครบวงจร',
      heroTitle: 'การเชื่อมต่อ HotelStack.io กับ PaymentHooks.com',
      heroSubtitle: 'ตัวรวมบริการชำระเงินแบบครบวงจรที่เชื่อมต่อ Stripe, PayPal, PaymentHooks, Square, Adyen และผู้ให้บริการกว่า 50 รายผ่าน API เดียว รองรับหลายสกุลเงิน การกำหนดเส้นทางอัตโนมัติ และการปฏิบัติตาม PCI ในตัว',
      benefits: [
        {
          title: 'ผู้ให้บริการชำระเงินกว่า 50 ราย',
          description: 'เชื่อมต่อ Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree และอีกหลายสิบรายผ่าน API แบบครบวงจรเดียว เพิ่มหรือเปลี่ยนผู้ให้บริการโดยไม่ต้องแก้ไขโค้ด',
        },
        {
          title: 'การกำหนดเส้นทางการชำระเงินอัจฉริยะ',
          description: 'การกำหนดเส้นทางอัตโนมัตินำธุรกรรมแต่ละรายการไปยังผู้ให้บริการที่เหมาะสมที่สุดตามสกุลเงิน ภูมิศาสตร์ อัตราความสำเร็จ และค่าธรรมเนียมการดำเนินการ เพิ่มอัตราการยอมรับสูงสุดพร้อมลดต้นทุน',
        },
        {
          title: 'การปฏิบัติตาม PCI DSS ระดับ 1',
          description: 'PaymentHooks จัดการข้อมูลบัตรที่ละเอียดอ่อนทั้งหมดในห้องนิรภัยที่ผ่านการรับรอง PCI HotelStack.io ไม่แตะต้องหมายเลขบัตรดิบ รับประกันการปฏิบัติตามอย่างสมบูรณ์โดยไม่มีภาระการตรวจสอบ',
        },
        {
          title: 'Webhook แบบเรียลไทม์',
          description: 'การแจ้งเตือน webhook ทันทีสำหรับเหตุการณ์การชำระเงินสำเร็จ ล้มเหลว คืนเงิน โต้แย้ง และการชำระบัญชี HotelStack.io อัปเดตบัญชีแขกและสถานะการจองโดยอัตโนมัติ',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'เชื่อมต่อบัญชี PaymentHooks',
          description: 'สร้างบัญชี PaymentHooks.com และเชื่อมต่อกับ HotelStack.io โดยใช้คีย์ API ของคุณ การตั้งค่าใช้เวลาไม่เกิน 5 นาทีจากแผงการตั้งค่า',
        },
        {
          step: 2,
          title: 'กำหนดค่าผู้ให้บริการชำระเงิน',
          description: 'เพิ่มข้อมูลประจำตัว Stripe, PayPal, PaymentHooks หรือเกตเวย์อื่นที่มีอยู่ในแดชบอร์ด PaymentHooks เปิดใช้งานผู้ให้บริการที่คุณต้องการรับ',
        },
        {
          step: 3,
          title: 'กำหนดกฎการกำหนดเส้นทาง',
          description: 'กำหนดตรรกะการกำหนดเส้นทาง: กำหนดเส้นทางตามสกุลเงิน ประเทศของแขก จำนวนธุรกรรม หรือให้ตัวเพิ่มประสิทธิภาพ AI เลือกผู้ให้บริการที่ดีที่สุดสำหรับแต่ละการชำระเงินโดยอัตโนมัติ',
        },
        {
          step: 4,
          title: 'รับชำระเงิน',
          description: 'แขกเห็นการชำระเงินที่ราบรื่นพร้อมวิธีการชำระเงินที่เปิดใช้งานทั้งหมด PaymentHooks กำหนดเส้นทางธุรกรรมแต่ละรายการและรายงานผลกลับไปยัง HotelStack.io แบบเรียลไทม์',
        },
      ],
      features: [
        {
          title: 'การประมวลผลหลายสกุลเงิน',
          description: 'รับชำระเงินในกว่า 150 สกุลเงินพร้อมการแปลงอัตโนมัติ แขกจ่ายในสกุลเงินท้องถิ่นของตน ในขณะที่คุณชำระบัญชีในสกุลเงินของคุณ',
        },
        {
          title: 'การกระทบยอดรายได้',
          description: 'รายงานการชำระบัญชีแบบครบวงจรจากผู้ให้บริการทั้งหมด จับคู่ธุรกรรมทุกรายการกับการจอง ค่าธรรมเนียมบัญชี หรือการคืนเงินด้วยการกระทบยอดคลิกเดียว',
        },
        {
          title: 'การกำหนดเส้นทางสำรอง',
          description: 'หากผู้ให้บริการชำระเงินหลักหยุดทำงานหรือปฏิเสธ PaymentHooks จะลองใหม่อัตโนมัติกับผู้ให้บริการสำรอง ไม่สูญเสียรายได้จากการหยุดทำงานของเกตเวย์',
        },
        {
          title: 'การจัดการการโต้แย้งการชำระเงิน',
          description: 'การแจ้งเตือนการโต้แย้งแบบรวมศูนย์ การส่งหลักฐาน และการติดตามข้อพิพาทจากผู้ให้บริการทั้งหมดจากแดชบอร์ดเดียว',
        },
        {
          title: 'บัตรแขกแบบโทเคน',
          description: 'จัดเก็บวิธีการชำระเงินของแขกอย่างปลอดภัยเป็นโทเคนสำหรับค่าธรรมเนียมไม่มาตามนัด การหักเงินสำหรับค่าใช้จ่ายเพิ่มเติม และการจองซ้ำโดยไม่ต้องกรอกข้อมูลบัตรใหม่',
        },
        {
          title: 'การวิเคราะห์ธุรกรรม',
          description: 'ติดตามอัตราการอนุมัติ เหตุผลการปฏิเสธ ต้นทุนการดำเนินการ และประสิทธิภาพของผู้ให้บริการในทุกเกตเวย์ด้วยแดชบอร์ดเรียลไทม์',
        },
      ],
      faqItems: [
        {
          question: 'PaymentHooks จะแทนที่เกตเวย์การชำระเงินที่มีอยู่ของฉันหรือไม่?',
          answer: 'ไม่ PaymentHooks วางอยู่บนเกตเวย์ที่มีอยู่ของคุณ คุณเก็บบัญชี Stripe, PayPal หรือ PaymentHooks ของคุณไว้ PaymentHooks รวมทั้งหมดไว้หลัง API เดียวและเพิ่มการกำหนดเส้นทางอัจฉริยะ การสำรอง และการกระทบยอด',
        },
        {
          question: 'ค่าธรรมเนียมสำหรับการใช้ PaymentHooks คืออะไร?',
          answer: 'PaymentHooks เรียกเก็บค่าธรรมเนียมต่อธุรกรรมเล็กน้อย (โดยทั่วไป 0.1-0.3%) บนค่าธรรมเนียมเกตเวย์ที่มีอยู่ของคุณ การกำหนดเส้นทางอัจฉริยะมักช่วยประหยัดได้มากกว่าโดยนำธุรกรรมไปยังผู้ให้บริการต้นทุนต่ำสุด',
        },
        {
          question: 'PaymentHooks ปฏิบัติตาม PCI หรือไม่?',
          answer: 'ใช่ PaymentHooks ได้รับการรับรอง PCI DSS ระดับ 1 ซึ่งเป็นระดับสูงสุดของการปฏิบัติตามความปลอดภัยการชำระเงิน ข้อมูลบัตรทั้งหมดถูกแปลงเป็นโทเคนและจัดเก็บในห้องนิรภัยที่ผ่านการรับรอง HotelStack.io ไม่จัดการหมายเลขบัตรดิบ',
        },
        {
          question: 'ฉันสามารถเพิ่มผู้ให้บริการชำระเงินใหม่ในภายหลังได้หรือไม่?',
          answer: 'ใช่ คุณสามารถเพิ่มหรือลบผู้ให้บริการชำระเงินได้ตลอดเวลาจากแดชบอร์ด PaymentHooks โดยไม่ต้องเปลี่ยนแปลงการกำหนดค่า HotelStack.io ของคุณ ผู้ให้บริการใหม่จะพร้อมใช้งานสำหรับแขกทันที',
        },
      ],
    },
    es: {
      partnerType: 'Agregador de Pagos Unificado',
      heroTitle: 'Integración de HotelStack.io con PaymentHooks.com',
      heroSubtitle: 'Agregador unificado de pasarelas de pago que conecta Stripe, PayPal, PaymentHooks, Square, Adyen y más de 50 proveedores a través de una sola API. Procesamiento multidivisa, enrutamiento automático y cumplimiento PCI integrados.',
      benefits: [
        {
          title: 'Más de 50 proveedores de pago',
          description: 'Conecta Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree y docenas más a través de una única API unificada. Añade o cambia proveedores sin modificar el código.',
        },
        {
          title: 'Enrutamiento inteligente de pagos',
          description: 'El enrutamiento automático dirige cada transacción al proveedor óptimo según la moneda, geografía, tasas de éxito y comisiones de procesamiento. Maximiza las tasas de aceptación minimizando los costes.',
        },
        {
          title: 'Cumplimiento PCI DSS Nivel 1',
          description: 'PaymentHooks gestiona todos los datos sensibles de tarjetas en su vault certificado PCI. HotelStack.io nunca toca números de tarjeta en bruto, garantizando el cumplimiento total sin la carga de auditorías.',
        },
        {
          title: 'Webhooks en tiempo real',
          description: 'Notificaciones webhook instantáneas para eventos de pago exitoso, fallido, reembolso, contracargo y liquidación. HotelStack.io actualiza los folios de huéspedes y los estados de reservas automáticamente.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Conectar cuenta PaymentHooks',
          description: 'Crea una cuenta en PaymentHooks.com y vincúlala a HotelStack.io usando tus claves API. La configuración lleva menos de 5 minutos desde el panel de Ajustes.',
        },
        {
          step: 2,
          title: 'Configurar proveedores de pago',
          description: 'Añade tus credenciales existentes de Stripe, PayPal, PaymentHooks u otras pasarelas en el panel de PaymentHooks. Activa los proveedores que deseas aceptar.',
        },
        {
          step: 3,
          title: 'Establecer reglas de enrutamiento',
          description: 'Define la lógica de enrutamiento: enruta por moneda, país del huésped, importe de la transacción, o deja que el optimizador de IA elija el mejor proveedor para cada pago automáticamente.',
        },
        {
          step: 4,
          title: 'Aceptar pagos',
          description: 'Los huéspedes ven un proceso de pago fluido con todos los métodos habilitados. PaymentHooks enruta cada transacción e informa los resultados a HotelStack.io en tiempo real.',
        },
      ],
      features: [
        {
          title: 'Procesamiento multidivisa',
          description: 'Acepta pagos en más de 150 monedas con conversión automática. Los huéspedes pagan en su moneda local mientras tú liquidas en la tuya.',
        },
        {
          title: 'Conciliación de ingresos',
          description: 'Informes de liquidación unificados en todos los proveedores. Relaciona cada transacción con una reserva, cargo de folio o reembolso con conciliación en un clic.',
        },
        {
          title: 'Enrutamiento de respaldo',
          description: 'Si un proveedor de pago principal está caído o rechaza, PaymentHooks reintenta automáticamente con un proveedor alternativo. Cero ingresos perdidos por caídas de pasarelas.',
        },
        {
          title: 'Gestión de contracargos',
          description: 'Alertas de contracargo centralizadas, envío de evidencias y seguimiento de disputas en todos los proveedores desde un único panel.',
        },
        {
          title: 'Tarjetas de huéspedes tokenizadas',
          description: 'Almacena de forma segura los métodos de pago de los huéspedes como tokens para cargos por no presentación, retenciones incidentales y reservas repetidas sin volver a introducir los datos de la tarjeta.',
        },
        {
          title: 'Analítica de transacciones',
          description: 'Rastrea tasas de aprobación, motivos de rechazo, costes de procesamiento y rendimiento de proveedores en todas las pasarelas con paneles en tiempo real.',
        },
      ],
      faqItems: [
        {
          question: '¿PaymentHooks reemplaza mi pasarela de pago existente?',
          answer: 'No. PaymentHooks se superpone a tus pasarelas existentes. Conservas tus cuentas de Stripe, PayPal o PaymentHooks. PaymentHooks las unifica detrás de una API y añade enrutamiento inteligente, respaldo y conciliación.',
        },
        {
          question: '¿Cuáles son las comisiones por usar PaymentHooks?',
          answer: 'PaymentHooks cobra una pequeña comisión por transacción (generalmente 0,1-0,3%) sobre las comisiones de tu pasarela existente. El enrutamiento inteligente suele ahorrar más que esto al dirigir las transacciones al proveedor de menor coste.',
        },
        {
          question: '¿PaymentHooks cumple con PCI?',
          answer: 'Sí. PaymentHooks está certificado PCI DSS Nivel 1, el nivel más alto de cumplimiento de seguridad en pagos. Todos los datos de tarjetas se tokenizan y almacenan en su vault certificado. HotelStack.io nunca maneja números de tarjeta en bruto.',
        },
        {
          question: '¿Puedo añadir nuevos proveedores de pago más adelante?',
          answer: 'Sí. Puedes añadir o eliminar proveedores de pago en cualquier momento desde el panel de PaymentHooks sin ningún cambio en tu configuración de HotelStack.io. Los nuevos proveedores están disponibles para los huéspedes de inmediato.',
        },
      ],
    },
    fr: {
      partnerType: 'Agrégateur de Paiement Unifié',
      heroTitle: 'Intégration HotelStack.io + PaymentHooks.com',
      heroSubtitle: 'Agrégateur unifié de passerelles de paiement connectant Stripe, PayPal, PaymentHooks, Square, Adyen et plus de 50 fournisseurs via une seule API. Traitement multi-devises, routage automatique et conformité PCI intégrés.',
      benefits: [
        {
          title: 'Plus de 50 fournisseurs de paiement',
          description: 'Connectez Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree et des dizaines d\'autres via une API unifiée. Ajoutez ou changez de fournisseurs sans modification de code.',
        },
        {
          title: 'Routage intelligent des paiements',
          description: 'Le routage automatique dirige chaque transaction vers le fournisseur optimal selon la devise, la géographie, les taux de réussite et les frais de traitement. Maximisez les taux d\'acceptation tout en minimisant les coûts.',
        },
        {
          title: 'Conformité PCI DSS Niveau 1',
          description: 'PaymentHooks gère toutes les données sensibles de carte dans son coffre certifié PCI. HotelStack.io ne touche jamais les numéros de carte bruts, garantissant une conformité totale sans charge d\'audit.',
        },
        {
          title: 'Webhooks en temps réel',
          description: 'Notifications webhook instantanées pour les événements de succès, d\'échec, de remboursement, de contestation et de règlement. HotelStack.io met à jour les folios clients et les statuts de réservation automatiquement.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Connecter le compte PaymentHooks',
          description: 'Créez un compte PaymentHooks.com et liez-le à HotelStack.io avec vos clés API. La configuration prend moins de 5 minutes depuis le panneau Paramètres.',
        },
        {
          step: 2,
          title: 'Configurer les fournisseurs de paiement',
          description: 'Ajoutez vos identifiants existants Stripe, PayPal, PaymentHooks ou d\'autres passerelles dans le tableau de bord PaymentHooks. Activez les fournisseurs que vous souhaitez accepter.',
        },
        {
          step: 3,
          title: 'Définir les règles de routage',
          description: 'Définissez la logique de routage : routez par devise, pays du client, montant de transaction, ou laissez l\'optimiseur IA choisir le meilleur fournisseur pour chaque paiement automatiquement.',
        },
        {
          step: 4,
          title: 'Accepter les paiements',
          description: 'Les clients voient un paiement fluide avec tous les moyens de paiement activés. PaymentHooks route chaque transaction et renvoie les résultats à HotelStack.io en temps réel.',
        },
      ],
      features: [
        {
          title: 'Traitement multi-devises',
          description: 'Acceptez les paiements dans plus de 150 devises avec conversion automatique. Les clients paient dans leur devise locale tandis que vous réglez dans la vôtre.',
        },
        {
          title: 'Rapprochement des revenus',
          description: 'Rapports de règlement unifiés sur tous les fournisseurs. Associez chaque transaction à une réservation, un débit de folio ou un remboursement en un clic.',
        },
        {
          title: 'Routage de secours',
          description: 'Si un fournisseur principal est indisponible ou refuse, PaymentHooks réessaie automatiquement avec un fournisseur alternatif. Zéro revenu perdu lors de pannes de passerelle.',
        },
        {
          title: 'Gestion des contestations',
          description: 'Alertes de contestation centralisées, soumission de preuves et suivi des litiges sur tous les fournisseurs depuis un seul tableau de bord.',
        },
        {
          title: 'Cartes clients tokenisées',
          description: 'Stockez en toute sécurité les moyens de paiement des clients sous forme de tokens pour les frais de non-présentation, les retenues incidentelles et les réservations répétées sans ressaisir les données de carte.',
        },
        {
          title: 'Analytique des transactions',
          description: 'Suivez les taux d\'approbation, les motifs de refus, les coûts de traitement et la performance des fournisseurs sur toutes les passerelles avec des tableaux de bord en temps réel.',
        },
      ],
      faqItems: [
        {
          question: 'PaymentHooks remplace-t-il ma passerelle de paiement existante ?',
          answer: 'Non. PaymentHooks se superpose à vos passerelles existantes. Vous conservez vos comptes Stripe, PayPal ou PaymentHooks. PaymentHooks les unifie derrière une API et ajoute le routage intelligent, le basculement et le rapprochement.',
        },
        {
          question: 'Quels sont les frais d\'utilisation de PaymentHooks ?',
          answer: 'PaymentHooks facture de petits frais par transaction (généralement 0,1-0,3 %) en plus de vos frais de passerelle existants. Le routage intelligent permet souvent d\'économiser davantage en dirigeant les transactions vers le fournisseur le moins coûteux.',
        },
        {
          question: 'PaymentHooks est-il conforme PCI ?',
          answer: 'Oui. PaymentHooks est certifié PCI DSS Niveau 1, le plus haut niveau de conformité en matière de sécurité des paiements. Toutes les données de carte sont tokenisées et stockées dans leur coffre certifié. HotelStack.io ne manipule jamais les numéros de carte bruts.',
        },
        {
          question: 'Puis-je ajouter de nouveaux fournisseurs de paiement ultérieurement ?',
          answer: 'Oui. Vous pouvez ajouter ou supprimer des fournisseurs de paiement à tout moment depuis le tableau de bord PaymentHooks sans aucune modification de votre configuration HotelStack.io. Les nouveaux fournisseurs deviennent disponibles pour les clients immédiatement.',
        },
      ],
    },
    de: {
      partnerType: 'Einheitlicher Zahlungsaggregator',
      heroTitle: 'HotelStack.io + PaymentHooks.com Integration',
      heroSubtitle: 'Einheitlicher Zahlungsgateway-Aggregator, der Stripe, PayPal, PaymentHooks, Square, Adyen und 50+ Anbieter über eine API verbindet. Mehrwährungs-Verarbeitung, automatisches Routing und PCI-Konformität inklusive.',
      benefits: [
        {
          title: '50+ Zahlungsanbieter',
          description: 'Verbinden Sie Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree und Dutzende weitere über eine einheitliche API. Fügen Sie Anbieter hinzu oder wechseln Sie ohne Code-Änderungen.',
        },
        {
          title: 'Intelligentes Zahlungsrouting',
          description: 'Automatisches Routing leitet jede Transaktion zum optimalen Anbieter basierend auf Währung, Geografie, Erfolgsraten und Verarbeitungsgebühren. Maximieren Sie Akzeptanzraten bei minimalen Kosten.',
        },
        {
          title: 'PCI DSS Level 1 Konformität',
          description: 'PaymentHooks verwaltet alle sensiblen Kartendaten in seinem PCI-zertifizierten Tresor. HotelStack.io berührt niemals Rohkartennummern und gewährleistet volle Konformität ohne Prüfungsaufwand.',
        },
        {
          title: 'Echtzeit-Webhooks',
          description: 'Sofortige Webhook-Benachrichtigungen für Zahlungserfolg, -fehler, Erstattung, Rückbuchung und Abrechnungsereignisse. HotelStack.io aktualisiert Gastkonten und Buchungsstatus automatisch.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'PaymentHooks-Konto verbinden',
          description: 'Erstellen Sie ein PaymentHooks.com-Konto und verknüpfen Sie es mit HotelStack.io über Ihre API-Schlüssel. Die Einrichtung dauert unter 5 Minuten im Einstellungsbereich.',
        },
        {
          step: 2,
          title: 'Zahlungsanbieter konfigurieren',
          description: 'Fügen Sie Ihre bestehenden Stripe-, PayPal-, PaymentHooks- oder andere Gateway-Zugangsdaten im PaymentHooks-Dashboard hinzu. Aktivieren Sie die Anbieter, die Sie akzeptieren möchten.',
        },
        {
          step: 3,
          title: 'Routing-Regeln festlegen',
          description: 'Definieren Sie Routing-Logik: nach Währung, Gastland, Transaktionsbetrag routen oder den KI-Optimierer den besten Anbieter für jede Zahlung automatisch wählen lassen.',
        },
        {
          step: 4,
          title: 'Zahlungen akzeptieren',
          description: 'Gäste sehen einen nahtlosen Checkout mit allen aktivierten Zahlungsmethoden. PaymentHooks routet jede Transaktion und meldet die Ergebnisse in Echtzeit an HotelStack.io zurück.',
        },
      ],
      features: [
        {
          title: 'Mehrwährungs-Verarbeitung',
          description: 'Akzeptieren Sie Zahlungen in 150+ Währungen mit automatischer Konvertierung. Gäste zahlen in ihrer Landeswährung, während Sie in Ihrer abrechnen.',
        },
        {
          title: 'Umsatzabstimmung',
          description: 'Einheitliche Abrechnungsberichte über alle Anbieter. Ordnen Sie jede Transaktion einer Buchung, einem Folia-Posten oder einer Erstattung per Ein-Klick-Abstimmung zu.',
        },
        {
          title: 'Failover-Routing',
          description: 'Wenn ein primärer Zahlungsanbieter ausgefallen ist oder ablehnt, wiederholt PaymentHooks automatisch mit einem alternativen Anbieter. Null verlorene Einnahmen durch Gateway-Ausfälle.',
        },
        {
          title: 'Rückbuchungsmanagement',
          description: 'Zentralisierte Rückbuchungswarnungen, Beweiseinreichung und Streitfallverfolgung über alle Anbieter von einem einzigen Dashboard.',
        },
        {
          title: 'Tokenisierte Gastkarten',
          description: 'Speichern Sie Zahlungsmethoden der Gäste sicher als Token für No-Show-Gebühren, Zusatzhalterungen und Wiederholungsbuchungen ohne erneute Karteneingabe.',
        },
        {
          title: 'Transaktionsanalyse',
          description: 'Verfolgen Sie Genehmigungsraten, Ablehnungsgründe, Verarbeitungskosten und Anbieterleistung über alle Gateways mit Echtzeit-Dashboards.',
        },
      ],
      faqItems: [
        {
          question: 'Ersetzt PaymentHooks mein bestehendes Zahlungsgateway?',
          answer: 'Nein. PaymentHooks liegt über Ihren bestehenden Gateways. Sie behalten Ihre Stripe-, PayPal- oder PaymentHooks-Konten. PaymentHooks vereint sie hinter einer API und ergänzt intelligentes Routing, Failover und Abstimmung.',
        },
        {
          question: 'Welche Gebühren fallen für PaymentHooks an?',
          answer: 'PaymentHooks berechnet eine kleine Gebühr pro Transaktion (typisch 0,1-0,3 %) zusätzlich zu Ihren bestehenden Gateway-Gebühren. Das intelligente Routing spart oft mehr, indem Transaktionen zum kostengünstigsten Anbieter geleitet werden.',
        },
        {
          question: 'Ist PaymentHooks PCI-konform?',
          answer: 'Ja. PaymentHooks ist PCI DSS Level 1 zertifiziert, das höchste Niveau der Zahlungssicherheits-Konformität. Alle Kartendaten werden tokenisiert und in ihrem zertifizierten Tresor gespeichert. HotelStack.io verarbeitet niemals Rohkartennummern.',
        },
        {
          question: 'Kann ich später neue Zahlungsanbieter hinzufügen?',
          answer: 'Ja. Sie können Zahlungsanbieter jederzeit im PaymentHooks-Dashboard hinzufügen oder entfernen, ohne Änderungen an Ihrer HotelStack.io-Konfiguration. Neue Anbieter stehen Gästen sofort zur Verfügung.',
        },
      ],
    },
    ja: {
      partnerType: '統合決済アグリゲーター',
      heroTitle: 'HotelStack.io + PaymentHooks.com 連携',
      heroSubtitle: 'Stripe、PayPal、PaymentHooks、Square、Adyen、50以上のプロバイダーを1つのAPIで接続する統合決済ゲートウェイアグリゲーター。多通貨処理、自動ルーティング、PCI準拠を標準搭載。',
      benefits: [
        {
          title: '50以上の決済プロバイダー',
          description: 'Stripe、PayPal、PaymentHooks、Square、Adyen、Worldpay、Braintreeをはじめ、数十のプロバイダーを単一の統合APIで接続。コード変更なしでプロバイダーを追加・切り替え可能。',
        },
        {
          title: 'インテリジェント決済ルーティング',
          description: '自動ルーティングが通貨、地域、成功率、処理手数料に基づいて各取引を最適なプロバイダーに振り分け。コスト最小化しながら承認率を最大化。',
        },
        {
          title: 'PCI DSS レベル1準拠',
          description: 'PaymentHooksはすべての機密カードデータをPCI認定ボールトで管理。HotelStack.ioは生のカード番号に一切触れず、監査負担なしで完全準拠を実現。',
        },
        {
          title: 'リアルタイムWebhook',
          description: '決済成功、失敗、返金、チャージバック、精算イベントに対する即時Webhook通知。HotelStack.ioはゲスト勘定書と予約ステータスを自動更新。',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'PaymentHooksアカウントの接続',
          description: 'PaymentHooks.comアカウントを作成し、APIキーを使用してHotelStack.ioに連携。設定パネルから5分以内にセットアップ完了。',
        },
        {
          step: 2,
          title: '決済プロバイダーの設定',
          description: 'PaymentHooksダッシュボードで既存のStripe、PayPal、PaymentHooks、その他のゲートウェイの認証情報を追加。受け付けたいプロバイダーを有効化。',
        },
        {
          step: 3,
          title: 'ルーティングルールの設定',
          description: 'ルーティングロジックを定義：通貨、ゲストの国、取引金額でルーティング、またはAIオプティマイザーが各決済に最適なプロバイダーを自動選択。',
        },
        {
          step: 4,
          title: '決済の受け付け開始',
          description: 'ゲストは有効化されたすべての決済手段でシームレスなチェックアウトを体験。PaymentHooksが各取引をルーティングし、結果をリアルタイムでHotelStack.ioに報告。',
        },
      ],
      features: [
        {
          title: '多通貨処理',
          description: '自動換算で150以上の通貨での決済を受け付け。ゲストは地元通貨で支払い、ホテルは自社通貨で精算。',
        },
        {
          title: '収益照合',
          description: '全プロバイダーを横断した統合精算レポート。ワンクリック照合で全取引を予約、勘定書費用、返金に対応付け。',
        },
        {
          title: 'フェイルオーバールーティング',
          description: 'プライマリ決済プロバイダーがダウンまたは拒否した場合、PaymentHooksが自動的に代替プロバイダーで再試行。ゲートウェイ障害による収益損失ゼロ。',
        },
        {
          title: 'チャージバック管理',
          description: '単一ダッシュボードで全プロバイダーのチャージバックアラート、証拠提出、紛争追跡を一元管理。',
        },
        {
          title: 'ゲストカードのトークン化',
          description: 'ゲストの決済手段をトークンとして安全に保存。ノーショー料金、付随費用、リピート予約でカード情報の再入力不要。',
        },
        {
          title: '取引アナリティクス',
          description: '全ゲートウェイの承認率、拒否理由、処理コスト、プロバイダーパフォーマンスをリアルタイムダッシュボードで追跡。',
        },
      ],
      faqItems: [
        {
          question: 'PaymentHooksは既存の決済ゲートウェイを置き換えますか？',
          answer: 'いいえ。PaymentHooksは既存のゲートウェイの上に位置します。Stripe、PayPal、PaymentHooksのアカウントはそのまま維持されます。PaymentHooksがそれらを1つのAPIの背後に統合し、インテリジェントルーティング、フェイルオーバー、照合機能を追加します。',
        },
        {
          question: 'PaymentHooksの利用料金はいくらですか？',
          answer: 'PaymentHooksは既存のゲートウェイ手数料に加え、1取引あたりわずかな手数料（通常0.1〜0.3%）を請求します。インテリジェントルーティングは最低コストのプロバイダーに取引を誘導することで、多くの場合この手数料以上の節約をもたらします。',
        },
        {
          question: 'PaymentHooksはPCI準拠ですか？',
          answer: 'はい。PaymentHooksはPCI DSS レベル1認定を取得しており、決済セキュリティコンプライアンスの最高レベルです。すべてのカードデータはトークン化され、認定ボールトに保存されます。HotelStack.ioは生のカード番号を一切扱いません。',
        },
        {
          question: '後から新しい決済プロバイダーを追加できますか？',
          answer: 'はい。HotelStack.ioの設定を変更することなく、PaymentHooksダッシュボードからいつでも決済プロバイダーを追加・削除できます。新しいプロバイダーはゲストにすぐに利用可能になります。',
        },
      ],
    },
    zh: {
      partnerType: '统一支付聚合商',
      heroTitle: 'HotelStack.io + PaymentHooks.com 集成',
      heroSubtitle: '统一支付网关聚合商，通过一个API连接 Stripe、PayPal、PaymentHooks、Square、Adyen 及50多家服务商。内置多币种处理、自动路由和PCI合规。',
      benefits: [
        {
          title: '50多家支付服务商',
          description: '通过单一统一API连接 Stripe、PayPal、PaymentHooks、Square、Adyen、Worldpay、Braintree 及数十家其他服务商。无需代码更改即可添加或切换服务商。',
        },
        {
          title: '智能支付路由',
          description: '自动路由根据货币、地理位置、成功率和处理费用将每笔交易定向到最优服务商。在最大化承兑率的同时最小化成本。',
        },
        {
          title: 'PCI DSS 一级合规',
          description: 'PaymentHooks 在其PCI认证金库中处理所有敏感卡片数据。HotelStack.io 从不接触原始卡号，确保完全合规，无审计负担。',
        },
        {
          title: '实时 Webhook',
          description: '支付成功、失败、退款、拒付和结算事件的即时 Webhook 通知。HotelStack.io 自动更新客人账单和预订状态。',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: '连接 PaymentHooks 账户',
          description: '创建 PaymentHooks.com 账户并使用您的API密钥将其与 HotelStack.io 关联。从设置面板完成设置不超过5分钟。',
        },
        {
          step: 2,
          title: '配置支付服务商',
          description: '在 PaymentHooks 仪表板中添加您现有的 Stripe、PayPal、PaymentHooks 或其他网关凭据。启用您希望接受的服务商。',
        },
        {
          step: 3,
          title: '设置路由规则',
          description: '定义路由逻辑：按货币、客人国家、交易金额路由，或让AI优化器自动为每笔支付选择最佳服务商。',
        },
        {
          step: 4,
          title: '开始接受付款',
          description: '客人看到包含所有已启用支付方式的无缝结账流程。PaymentHooks 路由每笔交易并实时将结果报告给 HotelStack.io。',
        },
      ],
      features: [
        {
          title: '多币种处理',
          description: '通过自动换算接受150多种货币的付款。客人以本地货币付款，您以本国货币结算。',
        },
        {
          title: '收入对账',
          description: '跨所有服务商的统一结算报告。通过一键对账将每笔交易与预订、账单费用或退款匹配。',
        },
        {
          title: '故障转移路由',
          description: '如果主要支付服务商宕机或拒绝，PaymentHooks 自动使用备用服务商重试。网关故障导致零收入损失。',
        },
        {
          title: '拒付管理',
          description: '从单一仪表板集中管理所有服务商的拒付警报、证据提交和争议跟踪。',
        },
        {
          title: '客人卡片令牌化',
          description: '将客人支付方式安全存储为令牌，用于未到店费用、附加费用预授权和重复预订，无需重新输入卡片信息。',
        },
        {
          title: '交易分析',
          description: '通过实时仪表板追踪所有网关的批准率、拒绝原因、处理成本和服务商绩效。',
        },
      ],
      faqItems: [
        {
          question: 'PaymentHooks 会取代我现有的支付网关吗？',
          answer: '不会。PaymentHooks 位于您现有网关之上。您保留 Stripe、PayPal 或 PaymentHooks 账户。PaymentHooks 将它们统一在一个API之后，并添加智能路由、故障转移和对账功能。',
        },
        {
          question: '使用 PaymentHooks 的费用是多少？',
          answer: 'PaymentHooks 在您现有网关费用之上收取每笔交易的小额费用（通常为0.1-0.3%）。智能路由通过将交易引导至成本最低的服务商，往往节省的金额超过这一费用。',
        },
        {
          question: 'PaymentHooks 符合PCI标准吗？',
          answer: '是的。PaymentHooks 通过 PCI DSS 一级认证，这是支付安全合规的最高级别。所有卡片数据在其认证金库中进行令牌化存储。HotelStack.io 从不处理原始卡号。',
        },
        {
          question: '我以后可以添加新的支付服务商吗？',
          answer: '可以。您可以随时从 PaymentHooks 仪表板添加或删除支付服务商，无需对 HotelStack.io 配置进行任何更改。新服务商立即对客人可用。',
        },
      ],
    },
    hi: {
      partnerType: 'एकीकृत भुगतान एग्रीगेटर',
      heroTitle: 'HotelStack.io + PaymentHooks.com इंटीग्रेशन',
      heroSubtitle: 'एकीकृत पेमेंट गेटवे एग्रीगेटर जो Stripe, PayPal, PaymentHooks, Square, Adyen और 50+ प्रोवाइडर्स को एक API से जोड़ता है। मल्टी-करेंसी प्रोसेसिंग, ऑटोमैटिक रूटिंग और PCI कम्प्लायंस बिल्ट-इन।',
      benefits: [
        {
          title: '50+ पेमेंट प्रोवाइडर',
          description: 'Stripe, PayPal, PaymentHooks, Square, Adyen, Worldpay, Braintree और दर्जनों अन्य को एकल एकीकृत API से जोड़ें। बिना कोड बदलाव के प्रोवाइडर जोड़ें या बदलें।',
        },
        {
          title: 'इंटेलिजेंट पेमेंट रूटिंग',
          description: 'ऑटोमैटिक रूटिंग करेंसी, भूगोल, सफलता दर और प्रोसेसिंग फीस के आधार पर प्रत्येक ट्रांजैक्शन को सर्वोत्तम प्रोवाइडर पर निर्देशित करती है। लागत कम करते हुए एक्सेप्टेंस रेट को अधिकतम करें।',
        },
        {
          title: 'PCI DSS लेवल 1 कम्प्लायंस',
          description: 'PaymentHooks अपने PCI-प्रमाणित वॉल्ट में सभी संवेदनशील कार्ड डेटा संभालता है। HotelStack.io कभी भी रॉ कार्ड नंबर नहीं देखता, ऑडिट बोझ के बिना पूर्ण कम्प्लायंस सुनिश्चित करता है।',
        },
        {
          title: 'रियल-टाइम Webhook',
          description: 'पेमेंट सफलता, विफलता, रिफंड, चार्जबैक और सेटलमेंट इवेंट के लिए तत्काल Webhook नोटिफिकेशन। HotelStack.io गेस्ट फोलियो और बुकिंग स्टेटस स्वचालित रूप से अपडेट करता है।',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'PaymentHooks अकाउंट कनेक्ट करें',
          description: 'PaymentHooks.com अकाउंट बनाएं और अपनी API keys का उपयोग करके इसे HotelStack.io से लिंक करें। सेटिंग्स पैनल से सेटअप 5 मिनट से कम समय में होता है।',
        },
        {
          step: 2,
          title: 'पेमेंट प्रोवाइडर कॉन्फिगर करें',
          description: 'PaymentHooks डैशबोर्ड में अपने मौजूदा Stripe, PayPal, PaymentHooks या अन्य गेटवे क्रेडेंशियल जोड़ें। वे प्रोवाइडर सक्षम करें जिन्हें आप स्वीकार करना चाहते हैं।',
        },
        {
          step: 3,
          title: 'रूटिंग नियम सेट करें',
          description: 'रूटिंग लॉजिक परिभाषित करें: करेंसी, गेस्ट देश, ट्रांजैक्शन राशि के अनुसार रूट करें, या AI ऑप्टिमाइज़र को प्रत्येक पेमेंट के लिए सर्वोत्तम प्रोवाइडर स्वचालित रूप से चुनने दें।',
        },
        {
          step: 4,
          title: 'पेमेंट स्वीकार करें',
          description: 'गेस्ट सभी सक्षम पेमेंट विधियों के साथ सहज चेकआउट देखते हैं। PaymentHooks प्रत्येक ट्रांजैक्शन रूट करता है और रीयल टाइम में HotelStack.io को परिणाम रिपोर्ट करता है।',
        },
      ],
      features: [
        {
          title: 'मल्टी-करेंसी प्रोसेसिंग',
          description: 'ऑटोमैटिक कन्वर्जन के साथ 150+ करेंसी में पेमेंट स्वीकार करें। गेस्ट अपनी स्थानीय करेंसी में भुगतान करते हैं जबकि आप अपनी करेंसी में सेटल करते हैं।',
        },
        {
          title: 'रेवेन्यू रिकंसिलिएशन',
          description: 'सभी प्रोवाइडर्स में एकीकृत सेटलमेंट रिपोर्ट। वन-क्लिक रिकंसिलिएशन से प्रत्येक ट्रांजैक्शन को बुकिंग, फोलियो चार्ज या रिफंड से मिलाएं।',
        },
        {
          title: 'फेलओवर रूटिंग',
          description: 'यदि प्राथमिक पेमेंट प्रोवाइडर डाउन है या अस्वीकार करता है, PaymentHooks स्वचालित रूप से वैकल्पिक प्रोवाइडर के साथ पुनः प्रयास करता है। गेटवे आउटेज से शून्य राजस्व हानि।',
        },
        {
          title: 'चार्जबैक मैनेजमेंट',
          description: 'एकल डैशबोर्ड से सभी प्रोवाइडर्स में केंद्रीकृत चार्जबैक अलर्ट, साक्ष्य सबमिशन और विवाद ट्रैकिंग।',
        },
        {
          title: 'टोकनाइज्ड गेस्ट कार्ड',
          description: 'कार्ड विवरण पुनः दर्ज किए बिना नो-शो चार्ज, इनसिडेंटल होल्ड और रिपीट बुकिंग के लिए गेस्ट पेमेंट विधियों को टोकन के रूप में सुरक्षित रूप से स्टोर करें।',
        },
        {
          title: 'ट्रांजैक्शन एनालिटिक्स',
          description: 'रीयल-टाइम डैशबोर्ड के साथ सभी गेटवे में अप्रूवल रेट, अस्वीकृति कारण, प्रोसेसिंग लागत और प्रोवाइडर प्रदर्शन ट्रैक करें।',
        },
      ],
      faqItems: [
        {
          question: 'क्या PaymentHooks मेरे मौजूदा पेमेंट गेटवे को रिप्लेस करता है?',
          answer: 'नहीं। PaymentHooks आपके मौजूदा गेटवे के ऊपर बैठता है। आप अपने Stripe, PayPal या PaymentHooks अकाउंट रखते हैं। PaymentHooks उन्हें एक API के पीछे एकीकृत करता है और इंटेलिजेंट रूटिंग, फेलओवर और रिकंसिलिएशन जोड़ता है।',
        },
        {
          question: 'PaymentHooks उपयोग करने की फीस क्या है?',
          answer: 'PaymentHooks आपकी मौजूदा गेटवे फीस के ऊपर प्रति ट्रांजैक्शन छोटी फीस (आमतौर पर 0.1-0.3%) लेता है। इंटेलिजेंट रूटिंग अक्सर इससे अधिक बचाती है क्योंकि यह ट्रांजैक्शन को सबसे कम लागत वाले प्रोवाइडर की ओर निर्देशित करती है।',
        },
        {
          question: 'क्या PaymentHooks PCI कम्प्लायंट है?',
          answer: 'हाँ। PaymentHooks PCI DSS लेवल 1 सर्टिफाइड है, जो पेमेंट सुरक्षा कम्प्लायंस का उच्चतम स्तर है। सभी कार्ड डेटा टोकनाइज़ किया जाता है और उनके सर्टिफाइड वॉल्ट में स्टोर किया जाता है। HotelStack.io कभी भी रॉ कार्ड नंबर नहीं संभालता।',
        },
        {
          question: 'क्या मैं बाद में नए पेमेंट प्रोवाइडर जोड़ सकता हूँ?',
          answer: 'हाँ। आप अपनी HotelStack.io कॉन्फिगरेशन में किसी बदलाव के बिना PaymentHooks डैशबोर्ड से किसी भी समय पेमेंट प्रोवाइडर जोड़ या हटा सकते हैं। नए प्रोवाइडर तुरंत गेस्ट के लिए उपलब्ध हो जाते हैं।',
        },
      ],
    },
  },
};
